/**
 * ErrorBoundary — top-level React error boundary.
 *
 * React's default behaviour when a render/lifecycle throws is to unmount
 * the entire tree, leaving a blank white page. That was actively hurting
 * user trust — e.g. the coin-settings save bug (iter 118) turned a simple
 * validation error into what looked like a total app crash.
 *
 * This boundary catches those crashes and renders a friendly fallback
 * instead: what went wrong, a "Try Again" reset, a "Reload Page" hard
 * refresh, and a "Go Home" escape hatch.
 *
 * Every crash also gets a short human-quotable Error ID (e.g.
 * "ERR-A4F7B21C"). The ID is:
 *   • shown to every user (harmless, useful for support tickets),
 *   • posted to /api/telemetry/client-error along with the crash payload,
 *   • greppable in the DB by admins.
 *
 * Technical details (message + stack + component stack + URL) are gated
 * to admin / sub_admin roles. Regular users never see raw stack traces
 * (they're a recon goldmine for anyone with malicious intent — internal
 * paths, framework versions, DB collection names all leak) and instead
 * see a "Create Support Ticket" button that routes to /help.
 *
 * Placed at the root of <App /> so it covers every route. Individual
 * routes can nest their own boundaries later if we want granular recovery.
 */
import React from 'react';
import { Button } from './ui/button';
import { AlertTriangle, RotateCw, Home, ChevronDown, ChevronUp, LifeBuoy } from 'lucide-react';

/** Random 8-hex Error ID prefixed with ERR-. Cryptographically random so
 *  two concurrent crashes never collide, but deliberately short so a user
 *  can read it out over the phone / paste into a ticket. */
const generateErrorId = () => {
  try {
    const bytes = new Uint8Array(4);
    (window.crypto || window.msCrypto).getRandomValues(bytes);
    return 'ERR-' + Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();
  } catch (_e) {
    // getRandomValues unavailable (very old browser / non-secure context).
    // Fall back to time-based; collision risk is fine given crash volume.
    return 'ERR-' + Date.now().toString(16).slice(-8).toUpperCase();
  }
};

/** Read the cached role from localStorage. AuthContext mirrors it there
 *  on every user change; we can't use the React context here because
 *  this boundary sits above the provider and may catch a crash *inside*
 *  the provider itself. */
const readCachedRole = () => {
  try {
    return localStorage.getItem('user_role');
  } catch (_e) {
    return null;
  }
};

const readCachedUserId = () => {
  try {
    return localStorage.getItem('user_id');
  } catch (_e) {
    return null;
  }
};

const isPrivilegedRole = (role) => role === 'admin' || role === 'sub_admin';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error) {
    // Flip into fallback mode. We don't stash the error here — that
    // happens in componentDidCatch which also has the component stack.
    // errorId is generated here so getDerivedStateFromError and
    // componentDidCatch see the same value.
    return { hasError: true, error, errorId: generateErrorId() };
  }

  componentDidCatch(error, errorInfo) {
    // Best-effort console log so the crash shows up in browser devtools
    // + any log-forwarding the environment has wired. Include the ID so
    // devs can match browser-console reports to server-side rows.
    console.error(`[ErrorBoundary] ${this.state.errorId}`, error, errorInfo);
    this.setState({ errorInfo });

    // Fire-and-forget telemetry to the backend so we can spot silent
    // client crashes in production without waiting for a bug report.
    // No await, no error handling — if this throws, it dies silently
    // (we're already in an error state, don't compound it).
    try {
      const role = readCachedRole();
      const userId = readCachedUserId();
      const body = JSON.stringify({
        error_id: this.state.errorId,
        error_type: 'react_boundary',
        message: String(error?.message || error),
        stack: String(error?.stack || '').slice(0, 4000),
        component_stack: String(errorInfo?.componentStack || '').slice(0, 4000),
        url: typeof window !== 'undefined' ? window.location.href : '',
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        user_id: userId,
        user_role: role,
        timestamp: new Date().toISOString(),
      });
      const backend = process.env.REACT_APP_BACKEND_URL;
      if (backend && typeof fetch !== 'undefined') {
        fetch(`${backend}/api/telemetry/client-error`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          keepalive: true,
        }).catch(() => { /* swallow — telemetry must never crash */ });
      }
    } catch (_e) { /* swallow */ }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, errorId: null, showDetails: false });
  };

  handleReload = () => {
    // Hard reload — clears any stale module state that might be causing
    // the render loop. This is the "when in doubt, blow it away" button.
    if (typeof window !== 'undefined') window.location.reload();
  };

  handleGoHome = () => {
    if (typeof window !== 'undefined') window.location.href = '/';
  };

  handleGoHelp = () => {
    if (typeof window !== 'undefined') window.location.href = '/help';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { error, errorInfo, errorId, showDetails } = this.state;
    const errorMessage = String(error?.message || error || 'Unknown error');
    const role = readCachedRole();
    const canSeeDetails = isPrivilegedRole(role);

    return (
      <div
        className="min-h-screen bg-slate-50 flex items-center justify-center p-4"
        data-testid="error-boundary-fallback"
      >
        <div className="max-w-lg w-full bg-white rounded-lg shadow-lg border border-slate-200 p-6 sm:p-8">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-lg font-semibold text-slate-900">
                Something went wrong
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                An unexpected error occurred while rendering this page. This is
                usually temporary — reloading or going back to the home page
                should recover the app.
              </p>
            </div>
          </div>

          {/* Always-visible Error ID pill. Harmless info, useful for tickets. */}
          {errorId && (
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <span>Reference:</span>
              <code
                className="px-2 py-0.5 rounded bg-slate-100 border border-slate-200 font-mono text-slate-700"
                data-testid="error-boundary-error-id"
              >
                {errorId}
              </code>
              <span className="text-slate-400">Please share this with support if the problem continues.</span>
            </div>
          )}

          <div className="flex flex-wrap gap-2 mt-6">
            <Button
              onClick={this.handleReload}
              className="bg-[#6366F1] hover:bg-[#4F46E5] text-white"
              data-testid="error-boundary-reload-btn"
            >
              <RotateCw className="w-4 h-4 mr-2" />
              Reload Page
            </Button>
            <Button
              variant="outline"
              onClick={this.handleGoHome}
              data-testid="error-boundary-home-btn"
            >
              <Home className="w-4 h-4 mr-2" />
              Go Home
            </Button>
            <Button
              variant="ghost"
              onClick={this.handleReset}
              data-testid="error-boundary-reset-btn"
              className="text-slate-600"
            >
              Try Again
            </Button>
          </div>

          <div className="mt-6 pt-4 border-t border-slate-100">
            {canSeeDetails ? (
              /* Admin / sub_admin view — full technical disclosure so they
                 can debug (and simulate) user-facing crashes without needing
                 to log in as the user themselves. */
              <>
                <button
                  type="button"
                  onClick={() => this.setState({ showDetails: !showDetails })}
                  className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
                  data-testid="error-boundary-details-toggle"
                >
                  {showDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  {showDetails ? 'Hide' : 'Show'} technical details
                  <span className="ml-1 text-[10px] uppercase tracking-wide text-violet-600 font-medium">
                    · admin only
                  </span>
                </button>
                {showDetails && (
                  <div
                    className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded text-xs font-mono text-slate-700 max-h-64 overflow-auto"
                    data-testid="error-boundary-details"
                  >
                    <p className="font-semibold text-rose-700 break-words mb-2">
                      {errorMessage}
                    </p>
                    {error?.stack && (
                      <pre className="whitespace-pre-wrap break-words text-[10px] text-slate-600">
                        {error.stack}
                      </pre>
                    )}
                    {errorInfo?.componentStack && (
                      <>
                        <p className="mt-2 font-semibold text-slate-800">Component stack:</p>
                        <pre className="whitespace-pre-wrap break-words text-[10px] text-slate-600">
                          {errorInfo.componentStack}
                        </pre>
                      </>
                    )}
                    <p className="mt-2 text-[10px] text-slate-400">
                      URL: {typeof window !== 'undefined' ? window.location.href : '—'}
                    </p>
                  </div>
                )}
              </>
            ) : (
              /* Regular user view — no stack traces. Direct them to support. */
              <Button
                variant="outline"
                onClick={this.handleGoHelp}
                className="text-slate-700 border-slate-300"
                data-testid="error-boundary-help-btn"
              >
                <LifeBuoy className="w-4 h-4 mr-2" />
                Create Support Ticket
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
